// src/components/common/SideInfoDrawer.js
// Filepath: // src/components/common/SideInfoDrawer.js

import React, { useMemo } from "react";
import { Drawer, Box, Typography } from "@mui/material";

/**
 * SideInfoDrawer
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - anchor?: "left" | "right" (default: "right")
 * - width?: number | string (default: 900)
 * - edgeGutter?: number (px; default: 48)
 * - maxContentWidth?: number (px; default: 1100)
 * - maxHeightPx?: number (default: 1080)
 * - title?: string
 * - subtitle?: string
 * - headerActions?: React.ReactNode
 * - containerId?: string (default: "root")
 * - keepMounted?: boolean (default: true)
 * - PaperProps?: object
 * - contentProps?: object
 * - headerProps?: object
 */
export default function SideInfoDrawer({
  open,
  onClose,
  anchor = "right",
  width = 900,
  edgeGutter = 48,
  maxContentWidth = 1100,
  maxHeightPx = 1080,
  title,
  subtitle,
  headerActions = null,
  containerId = "root",
  keepMounted = true,
  PaperProps,
  contentProps,
  headerProps,
  children,
}) {
  const container = useMemo(
    () => document.getElementById(containerId) || document.body,
    [containerId]
  );

  const clampedWidth =
    typeof width === "number"
      ? `min(${width}px, calc(100vw - ${edgeGutter * 2}px))`
      : width || `calc(100vw - ${edgeGutter * 2}px)`;

  const clampedHeight = `min(100vh, ${maxHeightPx}px)`;

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted, container }}
      PaperProps={{
        sx: {
          width: clampedWidth,
          height: clampedHeight,
          maxHeight: clampedHeight,
          boxSizing: "border-box",
          bgcolor: "background.paper",
          // Prevent right-anchor sub-pixel clipping on some zoom levels
          transform: "translateZ(0)",
          willChange: "transform",
        },
        ...PaperProps,
      }}
    >
      <Box sx={{ display: "grid", gridTemplateRows: "auto 1fr", height: "100%" }}>
        {(title || subtitle || headerActions) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            }}
            {...headerProps}
          >
            <Box sx={{ minWidth: 0 }}>
              {!!title && (
                <Typography
                  variant="h2"
                  sx={{
                    mb: subtitle ? 0.5 : 0,
                    whiteSpace: "normal",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                    hyphens: "auto",
                    fontSize: "clamp(28px, 3.5vw, 44px)",
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  {title}
                </Typography>
              )}
              {!!subtitle && (
                <Typography
                  sx={{
                    color: "text.secondary",
                    whiteSpace: "normal",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                    hyphens: "auto",
                    fontSize: "clamp(16px, 1.8vw, 22px)",
                    lineHeight: 1.25,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{headerActions}</Box>
          </Box>
        )}

        <Box
          sx={{
            p: 4,
            overflow: "hidden", // inner area handles scrolling
            fontSize: "clamp(16px, 1.6vw, 22px)",
            height: "100%",
          }}
          {...contentProps}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: maxContentWidth,
              mx: "auto",
              display: "grid",
              gap: 3,
              height: "100%",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
